import { defineStore } from 'pinia'
import { ref } from 'vue'

import type { ReportLevelSource } from '../models/level.types'
import { normalizeWorkspaceLevelSources } from '../service/columns/levels'

/** Workspace-scoped level metadata hydrated by account bootstrap's `dimensions` section. */
export const useReportLevelStore = defineStore('reportLevels', () => {
  const workspaceLevels = ref<Record<string, ReportLevelSource[]>>({})
  const runtimeMetrics = ref<Record<string, Record<string, string[]>>>({})

  function setWorkspaceLevels(value: Record<string, unknown[]>): void {
    workspaceLevels.value = normalizeWorkspaceLevelSources(value)
  }

  function levelsForWorkspace(
    workspaceId: string | number | null | undefined,
  ): ReportLevelSource[] {
    if (workspaceId === null || workspaceId === undefined) return []
    const key = String(workspaceId)
    const overlays = runtimeMetrics.value[key] ?? {}
    return (workspaceLevels.value[key] ?? []).map((level) => ({
      ...level,
      data: {
        ...level.data,
        metrics: [...new Set([...level.data.metrics, ...(overlays[level.partition_name] ?? [])])],
      },
    }))
  }

  function upsertRuntimeMetric(
    workspaceId: string | number,
    partitionName: string,
    field: string,
  ): void {
    const workspaceKey = String(workspaceId)
    const workspace = runtimeMetrics.value[workspaceKey] ?? {}
    runtimeMetrics.value = {
      ...runtimeMetrics.value,
      [workspaceKey]: {
        ...workspace,
        [partitionName]: [...new Set([...(workspace[partitionName] ?? []), field])],
      },
    }
  }

  function removeRuntimeMetric(workspaceId: string | number, field: string): void {
    const workspaceKey = String(workspaceId)
    const workspace = runtimeMetrics.value[workspaceKey]
    if (!workspace) return
    runtimeMetrics.value = {
      ...runtimeMetrics.value,
      [workspaceKey]: Object.fromEntries(
        Object.entries(workspace).map(([partition, fields]) => [
          partition,
          fields.filter((candidate) => candidate !== field),
        ]),
      ),
    }
  }

  function runtimeMetricFields(
    workspaceId: string | number | null | undefined,
    partitionName: string,
  ): string[] {
    if (workspaceId === null || workspaceId === undefined) return []
    return [...(runtimeMetrics.value[String(workspaceId)]?.[partitionName] ?? [])]
  }

  function resetState(): void {
    workspaceLevels.value = {}
    runtimeMetrics.value = {}
  }

  return {
    workspaceLevels,
    runtimeMetrics,
    setWorkspaceLevels,
    levelsForWorkspace,
    upsertRuntimeMetric,
    removeRuntimeMetric,
    runtimeMetricFields,
    resetState,
  }
})

export type ReportLevelStore = ReturnType<typeof useReportLevelStore>
